import {filter, isEqual} from 'lodash';

import {PrecogModel} from '../../../models/api/precogModel';
import {AppAction} from '../../appActions';
import {ResourceMap} from '../../appTypes';
import {DataGroupsActionsEnum} from '../dataGroupsActions';
import {ConversationPrecogActionsEnum, precogExpirationDelays} from './conversationPrecogsActions';

export type ConversationPrecogState = ResourceMap<ReadonlyArray<PrecogModel>>;

function hasPrecogUnexpired(precog: PrecogModel) {
  return Date.now() - precog.updatedAt < precogExpirationDelays[precog.type];
}

function cleanEmptyConversationPrecogs(
  conversationPrecogs: ConversationPrecogState,
): ConversationPrecogState {
  const cleanState: {[conversationId: number]: ReadonlyArray<PrecogModel>} = {};

  if (!conversationPrecogs) {
    return cleanState;
  }

  Object.keys(conversationPrecogs)
    .map(Number)
    .forEach((conversationId: number) => {
      const precogs = conversationPrecogs[conversationId];
      if (precogs && precogs.length > 0) {
        cleanState[conversationId] = precogs;
      }
    });

  return cleanState;
}

function keepCurrentStateIfEqual(
  state: ConversationPrecogState,
  newState: ConversationPrecogState,
): ConversationPrecogState {
  return isEqual(state, newState) ? state : newState;
}

function hasCorrespondingSentPrecog(precog: PrecogModel, sentPrecogs: ReadonlyArray<PrecogModel>): boolean {
  const {conversationId, teammateId, type} = precog;
  return sentPrecogs.some(
    (sentPrecog) =>
      sentPrecog.conversationId === conversationId &&
      sentPrecog.teammateId === teammateId &&
      sentPrecog.type === type,
  );
}

function getSentPrecogsState(
  state: ConversationPrecogState,
  userId: number,
  sentPrecogs: ReadonlyArray<PrecogModel>,
  conversationIds: ReadonlyArray<number>,
): ConversationPrecogState {
  const filteredState: {[conversationId: number]: ReadonlyArray<PrecogModel>} = {};

  conversationIds.forEach((conversationId: number) => {
    const conversationSentPrecogs = sentPrecogs.filter((p) => p.conversationId === conversationId);

    // Remove precogs having a corresponding sentPrecog.
    const precogs = (state[conversationId] || []).filter(
      (p) => !hasCorrespondingSentPrecog(p, conversationSentPrecogs),
    );
    filteredState[conversationId] = [...precogs, ...conversationSentPrecogs];
  });

  return filteredState;
}

function getCompletePrecogsState(
  state: ConversationPrecogState,
  userId: number,
  precogs: ReadonlyArray<PrecogModel>,
  conversationIds?: ReadonlyArray<number>,
): ConversationPrecogState {
  if (!conversationIds || conversationIds.length === 0) {
    return {};
  }

  const newPrecogsState: {[conversationId: number]: ReadonlyArray<PrecogModel>} = {};
  conversationIds.forEach((id) => {
    newPrecogsState[id] = precogs.filter(
      (p) => p.conversationId === id && hasPrecogUnexpired(p) && p.teammateId !== userId,
    );
  });

  return newPrecogsState;
}

function reducePrecogs(
  state: ResourceMap<ReadonlyArray<PrecogModel>>,
  userId: number,
  conversationIds: number[],
  conversationPrecogs?: ReadonlyArray<PrecogModel>,
  conversationSentPrecogs?: ReadonlyArray<PrecogModel>,
) {
  // Do a complete update of precogs.
  if (conversationPrecogs !== undefined) {
    const allPrecogs = [...conversationPrecogs, ...(conversationSentPrecogs || [])];

    return cleanEmptyConversationPrecogs({
      ...state,
      ...getCompletePrecogsState(state, userId, allPrecogs, conversationIds),
    });
  }

  // Nothing to do.
  if (conversationSentPrecogs === undefined) {
    return state;
  }

  // Add the sentPrecogs, and only remove the corresponding precogs.
  return cleanEmptyConversationPrecogs({
    ...state,
    ...getSentPrecogsState(state, userId, conversationSentPrecogs, conversationIds),
  });
}

export function conversationPrecogReducer(
  state: ConversationPrecogState,
  userId: number,
  action: AppAction,
): ConversationPrecogState {
  switch (action.type) {
    case DataGroupsActionsEnum.DATA_UPDATED:
      return keepCurrentStateIfEqual(
        state,
        reducePrecogs(
          state,
          userId,
          (action.payload.result.conversations || []).map((c) => c.id),
          action.payload.result.conversationPrecogs,
          action.payload.result.conversationSentPrecogs,
        ),
      );

    case ConversationPrecogActionsEnum.CONVERSATION_PRECOGS_UPDATED:
      return keepCurrentStateIfEqual(
        state,
        reducePrecogs(
          state,
          userId,
          [action.payload.conversationId],
          action.payload.precogs,
          action.payload.sentPrecogs,
        ),
      );

    case ConversationPrecogActionsEnum.CONVERSATION_PRECOG_UPDATED: {
      const updatedConversationPrecog = action.payload;
      const previousConversationPrecogs = state[updatedConversationPrecog.conversationId] || [];

      const updatedPrecogs = {
        ...state,
        [updatedConversationPrecog.conversationId]: [
          ...filter(
            previousConversationPrecogs,
            (p) =>
              !(
                p.teammateId === updatedConversationPrecog.teammateId &&
                p.type === updatedConversationPrecog.type
              ),
          ),
          updatedConversationPrecog,
        ],
      };

      return keepCurrentStateIfEqual(state, cleanEmptyConversationPrecogs(updatedPrecogs));
    }

    case ConversationPrecogActionsEnum.CONVERSATION_PRECOG_DELETED: {
      const isKeptPrecog = (p: PrecogModel) => {
        // undefined means that the type should be ignored.
        const isCorrectType = action.payload.precogType === undefined || p.type === action.payload.precogType;

        // Keep all the precogs except the one that is being deleted.
        return !(p.teammateId === action.payload.teammateId && isCorrectType);
      };

      return keepCurrentStateIfEqual(
        state,
        cleanEmptyConversationPrecogs({
          ...state,
          [action.payload.conversationId]: filter(state[action.payload.conversationId] || [], isKeptPrecog),
        }),
      );
    }

    default:
      return state;
  }
}
